import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    signal,
    untracked,
} from '@angular/core';
import {
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    Router,
    RouterLink,
} from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs';
import {
    MenuItem,
    PageSidebarService,
} from '../../../_shared/services/page-sidebar.service';
import {
    firstLeaf,
    NavLocation,
    NavLocationService,
} from '../../../_shared/services/nav-location.service';
import {
    REPORT_GROUP_PARAM,
    REPORTS_INDEX_ROUTE,
} from '../../../_shared/constants/report-catalog';
import { ReportGroupScrollService } from '../../../_shared/services/report-group-scroll.service';

/**
 * The RF-2798 desktop side nav (Sidebar.jsx in the design).
 *
 * - **Root level** lists the sections. A leaf (Overview, Calendar) navigates;
 *   a section with children drills in -- its items replace the list behind a
 *   Back row -- and opens the section's landing page.
 * - **Inside a section** items with children are accordions, one open at a
 *   time. Changing level closes any open group.
 * - The drill-in level and the open group follow the URL: when a navigation
 *   lands on another page (path), or moves the highlight to another section
 *   or group, the sidebar shows that section and opens that group
 *   (NavLocationService, nested aware). Only a query-only navigation on the
 *   same page -- a dashboard filter card, say -- leaves the user's own
 *   Back / accordion choice alone.
 * - Collapsed (icon-only, with title tooltips) persists through drill-in. It
 *   is the same `sidebar_collapsed` state the legacy nav uses.
 *
 * Shown only to super users while the new nav is behind its toggle; the
 * legacy `page-sidebar-menu` is untouched for everyone else.
 */
/** The URL without its query string or fragment. */
function pathOf(url: string): string {
    return url.split(/[?#]/)[0];
}

@UntilDestroy()
@Component({
    selector: 'app-client-sidebar',
    standalone: true,
    imports: [RouterLink],
    templateUrl: './client-sidebar.component.html',
    styleUrls: ['./client-sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.collapsed]': 'collapsed()',
    },
})
export class ClientSidebarComponent {
    private router = inject(Router);
    private navLocation = inject(NavLocationService);
    private sidebar = inject(PageSidebarService);
    private reportScroll = inject(ReportGroupScrollService);

    readonly items = this.sidebar.newNavColumns;
    readonly collapsed = this.sidebar.collapsed;

    /** The URL as of the last settled navigation. */
    private url = signal(this.router.url);

    /** The section the list is drilled into; null at the root level. */
    readonly viewSection = signal<MenuItem | null>(null);

    /**
     * True for a moment after the list changes level. The rows move under
     * the pointer, so the second click of a double-click would otherwise
     * land on whichever item now sits there (double-clicking Bookkeeping
     * opened Transaction Rules; double-clicking Back opened Overview).
     */
    readonly settling = signal(false);
    private settleTimer?: ReturnType<typeof setTimeout>;

    /** The one open accordion group inside `viewSection`, if any. */
    readonly openGroup = signal<MenuItem | null>(null);

    /**
     * The section and group the sidebar last followed the URL to. A
     * navigation that resolves to the same pair leaves the view alone.
     */
    private followed: {
        path: string | null;
        section: MenuItem | null;
        group: MenuItem | null;
    } = {
        path: null,
        section: null,
        group: null,
    };

    readonly location = computed<NavLocation>(() =>
        this.navLocation.resolve(this.url(), this.items()),
    );

    /** What the list renders: the drilled-in section's items, or the root. */
    readonly visibleItems = computed(
        () => this.viewSection()?.children ?? this.items(),
    );

    constructor() {
        // NavigationCancel/Error too: an unsaved-changes guard that keeps the
        // user on the page must not leave the nav on the page they didn't
        // reach (RF-2776).
        this.router.events
            .pipe(
                filter(
                    (event) =>
                        event instanceof NavigationEnd ||
                        event instanceof NavigationCancel ||
                        event instanceof NavigationError,
                ),
                untilDestroyed(this),
            )
            .subscribe((event) => {
                this.url.set(this.router.url);
                // A cancelled or failed navigation leaves the URL where it
                // was, but the click that started it already drilled the
                // list into the section it aimed for: always snap back.
                if (!(event instanceof NavigationEnd)) {
                    this.followUrl();
                    return;
                }
                // Re-drill on any page change, even within one section and
                // group (Back on Bookings overview, then an in-page link to
                // Leads must show Leads). Only a query-only navigation on the
                // same page (a dashboard filter card) keeps the user's Back
                // or hand-opened accordion -- unless it moves the highlight
                // to another section or group: the highlight follows the URL.
                const { section, group } = this.location();
                if (
                    pathOf(this.router.url) !== this.followed.path ||
                    section !== this.followed.section ||
                    (group ?? null) !== this.followed.group
                ) {
                    this.followUrl();
                }
            });

        // The tree is rebuilt when the client or the user loads; the old
        // object references are gone, so re-anchor on the new ones --
        // unconditionally, since nothing the user chose survives a rebuild.
        effect(
            () => {
                this.items();
                untracked(() => this.followUrl());
            },
            { allowSignalWrites: true },
        );
    }

    /** Drill into the section the URL resolves to and open its group. */
    private followUrl(): void {
        const location = this.location();
        const section = location.section;
        this.followed = {
            path: pathOf(this.url()),
            section,
            group: location.group ?? null,
        };
        this.viewSection.set(section?.children?.length ? section : null);
        this.openGroup.set(location.group ?? null);
    }

    /**
     * A sub-nav leaf click. The link navigates by itself; this only covers
     * the Reports group already in the URL, whose same-URL navigation the
     * router ignores, so the reports index is asked to re-scroll instead.
     */
    leafClicked(node: MenuItem): void {
        const slug = node.queryParams?.[REPORT_GROUP_PARAM];
        if (node.route !== REPORTS_INDEX_ROUTE || !slug) return;
        const tree = this.router.createUrlTree([node.route], {
            queryParams: node.queryParams,
        });
        const alreadyThere = this.router.isActive(tree, {
            paths: 'exact',
            queryParams: 'exact',
            fragment: 'ignored',
            matrixParams: 'ignored',
        });
        if (alreadyThere) this.reportScroll.request(String(slug));
    }

    /** Where clicking `node` goes: its own route, else its first leaf. */
    target(node: MenuItem): MenuItem | null {
        return node.route ? node : firstLeaf(node);
    }

    /**
     * Root-level section click: drill in. The link itself navigates. A
     * modified or non-primary click opens the section in another tab or
     * window (RouterLink leaves it to the browser), so this tab's page stays
     * put and its sidebar must too.
     */
    drillInto(section: MenuItem, event?: MouseEvent): void {
        if (
            event &&
            (event.button !== 0 ||
                event.ctrlKey ||
                event.metaKey ||
                event.shiftKey ||
                event.altKey)
        ) {
            return;
        }
        if (!section.children?.length) return;
        this.viewSection.set(section);
        this.openGroup.set(null);
        this.settle();
    }

    back(): void {
        this.viewSection.set(null);
        this.openGroup.set(null);
        this.settle();
    }

    /** Ignore pointer input on the list until a double-click has passed. */
    private settle(): void {
        this.settling.set(true);
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(
            () => this.settling.set(false),
            ClientSidebarComponent.SETTLE_MS,
        );
    }

    /** Longer than a double-click's gap, short enough not to be noticed. */
    static readonly SETTLE_MS = 400;

    toggleGroup(group: MenuItem): void {
        this.openGroup.update((current) => (current === group ? null : group));
    }

    isGroup(node: MenuItem): boolean {
        return !!node.children?.length;
    }

    /** Root level: the section the URL is in. */
    isSectionActive(node: MenuItem): boolean {
        return this.location().section === node;
    }

    /** Inside a section: the current leaf. */
    isItemActive(node: MenuItem): boolean {
        return this.location().item === node;
    }

    /** A group is lit while open, or while the current page is inside it. */
    isGroupActive(group: MenuItem): boolean {
        return this.openGroup() === group || this.location().group === group;
    }

    toggleCollapsed(): void {
        this.sidebar.toggle();
    }

    useLegacyNav(): void {
        this.sidebar.setNewNavEnabled(false);
    }
}
